/**
 * Écran « Utilisateurs & accès » (spec `docs/superpowers/specs/2026-09-19-utilisateurs-acces-design.md`).
 *
 * Contrats des trois lectures de l'écran (§8.2) et du CRUD minimal des familles de rôles (§5).
 * Rien ici n'est évalué à l'autorisation : ce sont des vues de lecture et un tag de rangement.
 */

import type { RbacRole } from './rbac.js'

/** Familles de rôles — `GET /rbac/families`. */
export interface RbacRoleFamily {
  id: number
  /** Clé métier stable (`boutique`, `rh`, …), citée par les URLs et les filtres. */
  key: string
  name: string
  description: string | null
  /** Jeton de couleur hexadécimal, pris dans les palettes du socle (§5.4). */
  color: string
  position: number
  roleCount: number
  /** Comptes distincts, non supprimés, affectés à au moins un rôle de la famille. */
  holderCount: number
  /** Rôles de la famille sans aucune permission v2 (rôles v1 convertis). */
  legacyRoleCount: number
}

export interface CreateRbacRoleFamilyRequest {
  key: string
  name: string
  description?: string | null
  color: string
  position?: number
}

export interface UpdateRbacRoleFamilyRequest {
  name?: string
  description?: string | null
  color?: string
  position?: number
}

/** Catalogue d'alertes de la spec §6, tel qu'exposé par l'API. */
export const RBAC_ALERT_IDS = [
  'A1',
  'A2',
  'A3',
  'A4',
  'A5',
  'A6',
  'A8',
  'A9',
  'A10',
  'A11',
] as const

export type RbacAlertId = (typeof RBAC_ALERT_IDS)[number]

export type RbacAlertSeverity = 'blocking' | 'review' | 'sensitive' | 'housekeeping'

/** Alertes portées par une ligne de l'axe « Par utilisateur » (et filtrables). */
export const RBAC_ADMIN_ALERT_IDS = ['A1', 'A2', 'A3', 'A5', 'A6', 'A8'] as const

export type RbacAdminAlertId = (typeof RBAC_ADMIN_ALERT_IDS)[number]

/** Libellé court d'une alerte, affiché en pastille de ligne. */
export const RBAC_ALERT_LABELS: Record<RbacAlertId, string> = {
  A1: 'Sans rôle',
  A2: 'Sans périmètre',
  A3: 'Droits inutilisés',
  A4: 'Sans titulaire',
  A5: 'Cumul inutile',
  A6: 'Accès complet',
  A8: 'Compte inactif',
  A9: 'Sans famille',
  A10: 'Clé jamais utilisée',
  A11: 'Clé sans expiration',
}

/** Phrase complète d'une alerte, affichée en infobulle. */
export const RBAC_ALERT_DESCRIPTIONS: Record<RbacAlertId, string> = {
  A1: 'Aucun rôle portant une permission : ce compte perdra tout accès au back-office.',
  A2: 'Une affectation restreinte sans aucune boutique : le rôle est en place, la liste manque.',
  A3: "Des droits effectifs n'ont pas été exercés sur la fenêtre observée.",
  A4: "Ce rôle n'a aucun titulaire : il peut être archivé.",
  A5: "Un accès complet cumulé avec d'autres rôles, qu'il couvre déjà.",
  A6: 'Accès complet : un rôle qui porte au moins 90 % de la matrice, comme Full Access.',
  A8: 'Aucune connexion et aucun usage de permission sur la fenêtre observée.',
  A9: "Ce rôle n'est rangé dans aucune famille.",
  A10: "Clé d'API jamais utilisée depuis plus de 30 jours.",
  A11: "Clé d'API sans date d'expiration.",
}

export interface RbacOverviewAlert {
  id: RbacAlertId
  severity: RbacAlertSeverity
  count: number
}

/**
 * `GET /rbac/overview` — les agrégats de la rangée de widgets, en une requête.
 *
 * Les agrégats **de rôles** sont **absents** (et non à zéro) pour un acteur sans
 * `read roles-admin.roles.role` : une tuile à zéro se lirait « c'est bon », pas « interdit ».
 */
export interface RbacAccessOverview {
  generatedAt: string
  usageWindowDays: number
  recentWindowDays: number
  taxonomy: { permissionCouples: number; fullAccessThreshold: number }
  counts: {
    admins: number
    roles?: number
    families?: number
    adminsWithoutRole: number
    assignmentsWithoutScope: number
    adminsWithoutScope: number
    rolesWithoutHolder?: number
    rolesWithoutFamily?: number
    inactiveAdmins: number
    fullAccessAdmins?: number
    unusedPermissions?: { value: number; admins: number }
    apiKeys: { active: number; neverUsed: number; withoutExpiry: number }
    adminsCreatedRecently: { value: number; previous: number }
  }
  alerts: RbacOverviewAlert[]
}

/** Affectation d'un compte, telle que la rend `GET /rbac/admins`. */
export interface RbacAccessAdminRole {
  roleId: number
  name: string
  familyKey: string | null
  allStores: boolean
  storeIds: number[]
  storeCount: number
  /** Le rôle porte-t-il au moins une permission v2 ? Un rôle v1 pur n'ouvre rien au J+1. */
  grantsV2: boolean
}

export interface RbacAccessAdmin {
  id: number
  email: string
  firstName: string
  lastName: string
  createdAt: string | null
  /** Dernière connexion = `max(admin_tokens.created_at)` (recommandation Q9, sans migration). */
  lastLoginAt: string | null
  lastPermissionUseAt: string | null
  /** Bits hérités, conservés tant que le régime v1 peut revenir (lot F12). */
  legacyRights: number
  roles: RbacAccessAdminRole[]
  effectivePermissionCount: number
  unusedPermissionCount: number
  apiKeyCount: number
  alerts: RbacAdminAlertId[]
}

export interface RbacAccessAdminsPage {
  total: number
  limit: number
  offset: number
  usageWindowDays: number
  items: RbacAccessAdmin[]
}

/**
 * `GET /rbac/roles-without-holder` — liste des rôles sans titulaire non récents (alerte A4).
 *
 * Les mêmes critères que `counts.rolesWithoutHolder` de `GET /rbac/overview` : aucun compte
 * non supprimé affecté, et rôle créé depuis plus de `recentWindowDays` jours.
 */
export interface RbacRolesWithoutHolderResponse {
  recentWindowDays: number
  roles: RbacRole[]
}

export type RbacAccessAdminsScope = 'all' | 'none' | 'partial'

export type RbacAccessAdminsSort =
  | 'alerts'
  | 'name'
  | 'email'
  | 'lastUsage'
  | 'lastLogin'
  | 'createdAt'

export interface RbacAccessAdminsParams {
  q?: string
  familyKey?: string
  roleId?: number
  storeId?: number
  alert?: RbacAdminAlertId
  hasRole?: boolean
  scope?: RbacAccessAdminsScope
  usageWindowDays?: number
  sort?: RbacAccessAdminsSort
  limit?: number
  offset?: number
}

/** Fenêtres d'usage proposées par le sélecteur de l'en-tête (défaut 90 jours). */
export const RBAC_USAGE_WINDOW_DAYS = [30, 90, 180, 365] as const

export const DEFAULT_RBAC_USAGE_WINDOW_DAYS = 90

/** Ligne de la revue des habilitations d'un compte (`GET /rbac/usage/admins/:id`, lot F10). */
export interface RbacAdminPermissionUsageLine {
  resource: string
  action: string
  firstUsedAt: string | null
  lastUsedAt: string | null
  /** Aucun usage enregistré sur la fenêtre : le droit ne sert (plus) à rien. */
  unused: boolean
}

export interface RbacAdminPermissionUsageReport {
  admin: { id: number; email: string; firstName: string; lastName: string }
  days: number
  permissions: RbacAdminPermissionUsageLine[]
  unusedCount: number
}
